import React, { useContext, useEffect, useRef, useState } from "react";
import "./styles.scss";
import { Formik } from "formik";
import { ApplicationContext } from "shared";
import { CATEGORIES_TYPES } from "common";
import { addNewItem, saveUserProfile } from "services";
import { getAllProfileRecords } from "services/firebaseServices";
import { CircularProgress } from "@mui/material";
import { MedicalCard } from "components/medical-card";
import { MaterialInputField, PhoneInputField } from "components";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { Button, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import dayjs from "dayjs";
import {
  GenderList,
  constants,
  countriesList,
  emergencyRelationshipList,
} from "common";
import { nationality } from "common/nationality";
import { civilStatus } from "common/civilstatus";
import { signUpStep2Schema, personalInfoModalSchema, personalInfoInsuranceSchema } from "common/schemas";
import { getProfile, setSecondaryProfile, clearStorage } from "utilities";
import { images } from "common/images";
import { useNavigate } from "react-router-dom";
import { routePaths } from "routes";

const converDateFormate = (dateString) => {
  const d = new Date(dateString);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${d.getFullYear()}`;
};

// Parse a date string from DB into a Date object.
// Handles DD/MM/YYYY, MM/DD/YYYY (legacy en-US), and single-digit month/day variants.
const parseBirthDate = (val) => {
  if (!val) return null;
  if (typeof val === "string" && val.includes("/")) {
    const parts = val.split("/");
    if (parts.length === 3) {
      const a = Number(parts[0]);
      const b = Number(parts[1]);
      const y = Number(parts[2]);
      if (a > 12) return new Date(y, b - 1, a); // DD/MM/YYYY
      if (b > 12) return new Date(y, a - 1, b); // MM/DD/YYYY (legacy)
      return new Date(y, b - 1, a); // ambiguous → DD/MM/YYYY
    }
  }
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
};

const ALL_STEPS = [
  {
    id: "personal",
    kind: "profile",
    label: "Personal",
    title: "Personal Information",
    subtitle: "Tell us a bit about yourself",
    icon: "👤",
    iconBg: "#EFF3FC",
  },
  {
    id: "address",
    kind: "profile",
    label: "Address",
    title: "Your Address",
    subtitle: "Where should we send your medical card?",
    icon: "🏠",
    iconBg: "#FCF0CE",
  },
  {
    id: "insurance",
    kind: "profile",
    label: "Insurance",
    skippable: true,
    title: "Insurance Details",
    subtitle: "Add your insurance information (optional)",
    icon: "🛡️",
    iconBg: "#F7D1F4",
  },
  {
    id: "emergency",
    kind: "profile",
    label: "Emergency",
    title: "Emergency Contacts",
    subtitle: "Who should we contact in an emergency?",
    icon: "🚨",
    iconBg: "#FFE5E5",
  },
  {
    id: "diagnoses",
    kind: "medical",
    label: "Diagnoses",
    type: CATEGORIES_TYPES.diagnoses,
    title: "Add Diagnoses",
    subtitle: "Search and add your diagnoses based on ICD-11",
    iconImg: images.allergyIcon,
    iconBg: "#F7D1F4",
  },
  {
    id: "medicines",
    kind: "medical",
    label: "Medicines",
    type: CATEGORIES_TYPES.medicines,
    title: "Add Medicines",
    subtitle: "Search and add your medications based on ATC",
    iconImg: images.medicineIcon,
    iconBg: "#D9E2FC",
  },
  {
    id: "allergies",
    kind: "medical",
    label: "Allergies",
    type: CATEGORIES_TYPES.allergies,
    title: "Add Allergies",
    subtitle: "Search and add any known allergies",
    iconImg: images.diagnoseIcon,
    iconBg: "#FCF0CE",
  },
  {
    id: "vaccines",
    kind: "medical",
    label: "Vaccines",
    type: CATEGORIES_TYPES.vaccines,
    title: "Add Vaccines",
    subtitle: "Search and add your vaccinations",
    iconImg: images.vaccineIcon,
    iconBg: "#D1FDBD",
  },
  {
    id: "order",
    kind: "order",
    label: "Order Card",
    title: "Review & Order Card",
    subtitle: "Your card is ready — review the details and place your order",
    icon: "🪪",
    iconBg: "#EFF3FC",
  },
];

export const isPersonalInfoComplete = (d) =>
  !!(
    d?.firstName &&
    d?.lastName &&
    d?.birthDate &&
    d?.gender &&
    d?.nationality
  );

export const isAddressComplete = (d) =>
  !!(
    d?.postalAddress?.[0]?.info &&
    d?.postalAddress?.[0]?.city &&
    d?.postalAddress?.[0]?.state &&
    d?.postalAddress?.[0]?.zipCode &&
    d?.postalAddress?.[0]?.country
  );

export const isInsuranceComplete = (d) =>
  Array.isArray(d?.insurance) &&
  d.insurance.some(
    (i) =>
      (i?.type && i.type !== "Choose") ||
      (i?.company && i.company.trim() !== "") ||
      (i?.policy && i.policy.trim() !== "") ||
      (i?.phone && i.phone.trim() !== ""),
  );

export const isEmergencyComplete = (d) =>
  !!(
    d?.emergencyContact?.[0]?.name &&
    d?.emergencyContact?.[0]?.phone &&
    d?.emergencyContact?.[0]?.relationship
  );

const getStartStep = (userData) => {
  if (!isPersonalInfoComplete(userData)) return 0;
  if (!isAddressComplete(userData)) return 1;
  if (!isInsuranceComplete(userData)) return 2;
  if (!isEmergencyComplete(userData)) return 3;
  return 4;
};

export const CardOrderWizard = ({
  show,
  onClose,
  onOrder,
  orderLoading,
  categoryListing,
  onStepComplete,
}) => {
  // When insurance is on, name + DOB are collected at signup — hide them here
  // (values stay pre-filled and are still submitted).
  const insuranceEnabled = process.env.REACT_APP_INSURANCE_ENABLED === "true";
  const {
    allergies,
    medicines,
    diagnoses,
    vaccines,
    getTheUpdatedData,
    emergencyfields,
    setEmergencyFields,
    emergencyFieldCount,
    setEmergencyFieldCount,
    insurancefields,
    setInsuranceFields,
    insuranceFieldCount,
    setInsuranceFieldCount,
    userData,
    setUserData,
  } = useContext(ApplicationContext);

  // When insurance is on, name + DOB are gathered at sign-up. Hide (and skip
  // validating) them here ONLY if the profile already carries them; otherwise
  // show the fields so the user can complete them rather than being silently
  // blocked on hidden required fields.
  const nameDobKnown = !!(
    userData?.firstName && userData?.lastName && userData?.birthDate
  );
  const hideNameDob = insuranceEnabled && nameDobKnown;

  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isOrderProcessing, setIsOrderProcessing] = useState(false);

  // Medical step state
  const [searchedValue, setSearchedValue] = useState("");
  const [list, setList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [selectedDescription, setSelectedDescription] = useState("");
  const [dataLoader, setDataLoader] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [addedInStep, setAddedInStep] = useState([]);

  // Profile step state
  const formikRef = useRef(null);
  const [profileSaving, setProfileSaving] = useState(false);
  // Per-medical-step "I don't have any" confirmation
  const [confirmedEmpty, setConfirmedEmpty] = useState(false);
  const [medicalNextError, setMedicalNextError] = useState(false);
  const searchDebounceRef = useRef(null);

  useEffect(() => {
    if (show) {
      setCurrentStep(getStartStep(userData));
      setAddedInStep([]);
      setIsOrderProcessing(false);
    }
  }, [show]);

  useEffect(() => {
    const step = ALL_STEPS[currentStep];
    setConfirmedEmpty(false);
    setMedicalNextError(false);
    if (show && step?.kind === "medical") {
      setSearchedValue("");
      setList([]);
      setFilteredList([]);
      setSelectedDescription("");
      setAddedInStep([]);
      loadItems(currentStep);
    }
  }, [currentStep, show]);

  const advance = () =>
    setCurrentStep((prev) => Math.min(prev + 1, ALL_STEPS.length - 1));

  const handleNext = () => {
    const step = ALL_STEPS[currentStep];
    if (step.kind === "profile") {
      formikRef.current?.submitForm();
    } else if (step.kind === "medical") {
      const currentSaved =
        categoryListing?.find((c) => c.type === step.type)?.dataListing
          ?.length || 0;
      const hasItems = currentSaved > 0 || addedInStep.length > 0;
      if (!hasItems && !confirmedEmpty) {
        setMedicalNextError(true);
        return;
      }
      advance();
    } else {
      advance();
    }
  };

  const handleBack = () => setCurrentStep((prev) => Math.max(prev - 1, 0));

  const handleOrderClick = async () => {
    setIsOrderProcessing(true);
    try {
      console.log("Placing order with data:", { userData, categoryListing });
      await onOrder();
    } catch (error) {
      console.error("Order failed:", error);
    } finally {
      setIsOrderProcessing(false);
    }
  };

  // ── Medical step helpers ──────────────────────────────────────────────────

  const setLoaderDetails = (state, message) => {
    setDataLoader(state);
    setLoaderMessage(state ? message : "");
  };

  const loadItems = async (stepIndex) => {
    setLoaderDetails(true, "Retrieving data...");
    const type = ALL_STEPS[stepIndex].type;
    let items = [];
    try {
      switch (type) {
        case CATEGORIES_TYPES.allergies:
          items = allergies?.length > 0
            ? allergies
            : await getTheUpdatedData(CATEGORIES_TYPES.allergies);
          break;
        case CATEGORIES_TYPES.medicines:
          items = medicines?.length > 0
            ? medicines
            : await getTheUpdatedData(CATEGORIES_TYPES.medicines);
          break;
        case CATEGORIES_TYPES.diagnoses:
          items = diagnoses?.length > 0
            ? diagnoses
            : await getTheUpdatedData(CATEGORIES_TYPES.diagnoses);
          break;
        case CATEGORIES_TYPES.vaccines:
          items = vaccines?.length > 0
            ? vaccines
            : await getTheUpdatedData(CATEGORIES_TYPES.vaccines);
          break;
        default:
          items = [];
      }
    } catch (err) {
      console.error(`[Wizard] loadItems error for ${type}:`, err.message || err);
    }
    setList(Array.isArray(items) ? items : []);
    setLoaderDetails(false, "");
  };

  const searchFromList = (searchText, type) => {
    if (type === CATEGORIES_TYPES.diagnoses) {
      const fetchDiagnoses = async () => {
        setLoaderDetails(true, "Wait a moment...");
        localStorage.setItem("search-text", searchText);
        const result = await getAllProfileRecords(CATEGORIES_TYPES.diagnoses);
        setList(result);
        setFilteredList(result);
        setLoaderDetails(false, "");
      };
      if (searchText.length >= 2) fetchDiagnoses();
      else if (searchText.length === 1) {
        setList([]);
        setFilteredList([]);
      }
    } else {
      const safeList = Array.isArray(list) ? list : [];
      console.log(`[Wizard] Searching ${safeList.length} items for "${searchText}"`);
      setFilteredList(
        safeList.filter(
          ({ description }) =>
            typeof description === "string" &&
            description.toLowerCase().includes(searchText.toLowerCase()),
        ),
      );
    }
  };

  const saveItem = async (itemDescription) => {
    if (!itemDescription) return;
    setSaving(true);
    try {
      await addNewItem(ALL_STEPS[currentStep].type, itemDescription);
      setAddedInStep((prev) => [...prev, itemDescription]);
      setSearchedValue("");
      setSelectedDescription("");
      setFilteredList([]);
      if (onStepComplete) onStepComplete();
    } catch (err) {
      console.error("[Wizard] saveItem error:", err.message || err);
    } finally {
      setSaving(false);
    }
  };

  // ── Profile submit handlers ───────────────────────────────────────────────

  const handlePersonalInfoSubmit = async (values) => {
    setProfileSaving(true);
    try {
      const profileId = getProfile()?.uid;
      const updatedData = {
        firstName: values.firstName,
        lastName: values.lastName,
        birthDate: converDateFormate(values.dob),
        nationality: values.nationality,
        gender: values.gender,
        ssn: values.socialNum,
        organDonation: values.organDonar,
        relationShip: values.relationShip,
      };
      await saveUserProfile({ profileId, ...updatedData });
      const updated = { ...userData, ...updatedData };
      setSecondaryProfile(updated);
      setUserData(updated);
      advance();
    } catch (err) {
      console.error(err);
    } finally {
      setProfileSaving(false);
    }
  };

  const handleAddressSubmit = async (values) => {
    setProfileSaving(true);
    try {
      const profileId = getProfile()?.uid;
      const updatedData = {
        postalAddress: [
          {
            info: values.address,
            city: values.city,
            state: values.state,
            zipCode: values.zipCode,
            country: values.country,
          },
        ],
      };
      await saveUserProfile({ profileId, ...updatedData });
      const updated = { ...userData, ...updatedData };
      setSecondaryProfile(updated);
      setUserData(updated);
      advance();
    } catch (err) {
      console.error(err);
    } finally {
      setProfileSaving(false);
    }
  };

  const handleInsuranceSubmit = async (values) => {
    setProfileSaving(true);
    try {
      const profileId = getProfile()?.uid;
      const extra = [];
      for (let i = 1; i <= insuranceFieldCount; i++) {
        const obj = {};
        if (insurancefields[`type_${i}`])
          obj.type = insurancefields[`type_${i}`];
        if (insurancefields[`company_${i}`])
          obj.company = insurancefields[`company_${i}`];
        if (insurancefields[`policy_${i}`])
          obj.policy = insurancefields[`policy_${i}`];
        if (insurancefields[`phone_${i}`])
          obj.phone = insurancefields[`phone_${i}`];
        if (Object.keys(obj).length > 0) extra.push(obj);
      }
      const insurances = [];
      const hasMain =
        (values.insuranceType && values.insuranceType !== "Choose") ||
        values.insuranceCompany?.trim() ||
        values.policyNumber?.trim() ||
        values.emergencyNumber?.trim();
      if (hasMain) {
        insurances.push({
          type: values.insuranceType === "Choose" ? "" : values.insuranceType,
          company: values.insuranceCompany || "",
          policy: values.policyNumber || "",
          phone: values.emergencyNumber || "",
        });
      }
      const allIns = [...insurances, ...extra];
      await saveUserProfile({ profileId, insurance: allIns });
      const updated = { ...userData, insurance: allIns };
      setSecondaryProfile(updated);
      setUserData(updated);
      advance();
    } catch (err) {
      console.error(err);
    } finally {
      setProfileSaving(false);
    }
  };

  const handleEmergencySubmit = async (values) => {
    setProfileSaving(true);
    try {
      const profileId = getProfile()?.uid;
      const extraRaw =
        emergencyFieldCount > 0 && Object.keys(emergencyfields).length > 0
          ? [emergencyfields]
          : [];
      const extra = extraRaw.flatMap((item) => {
        const groups = {};
        for (const key in item) {
          const [name, idx] = key.split("_");
          if (!groups[idx]) groups[idx] = {};
          if (name === "name") groups[idx].name = item[key];
          if (name === "phone") groups[idx].phone = item[key];
          if (name === "relation") groups[idx].relationship = item[key];
        }
        return Object.values(groups);
      });
      const emergencyContacts = [
        {
          name: values.emergencyName,
          phone: values.emergencyPhone,
          relationship: values.emergencyRelation,
        },
        ...extra,
      ];
      const updatedData = { emergencyContact: emergencyContacts };
      await saveUserProfile({ profileId, ...updatedData });
      const updated = { ...userData, ...updatedData };
      setSecondaryProfile(updated);
      setUserData(updated);
      advance();
    } catch (err) {
      console.error(err);
    } finally {
      setProfileSaving(false);
    }
  };

  // ── Emergency extra-field helpers ─────────────────────────────────────────

  const validateEmergencyForm = (values) => {
    const errors = {};
    if (
      !values.emergencyName?.trim() ||
      values.emergencyName.trim().length < 3
    ) {
      errors.emergencyName = "Name must be at least 3 characters";
    } else if (!/^[a-zA-Z\s]+$/.test(values.emergencyName)) {
      errors.emergencyName = "Name can only contain letters and spaces";
    }
    if (
      !values.emergencyPhone?.trim() ||
      values.emergencyPhone.replace(/\D/g, "").length < 7
    ) {
      errors.emergencyPhone = "Phone number must have at least 7 digits";
    }
    if (!values.emergencyRelation?.trim()) {
      errors.emergencyRelation = "Relationship is required";
    }
    for (let i = 1; i <= emergencyFieldCount; i++) {
      const n = emergencyfields[`name_${i}`] || "";
      const p = emergencyfields[`phone_${i}`] || "";
      const r = emergencyfields[`relation_${i}`] || "";
      if (!n.trim() || n.trim().length < 3 || !/^[a-zA-Z\s]+$/.test(n))
        errors[`name_${i}`] = "Valid name required";
      if (!p.trim() || p.replace(/\D/g, "").length < 7)
        errors[`phone_${i}`] = "Valid phone required";
      if (!r.trim()) errors[`relation_${i}`] = "Relationship required";
    }
    return errors;
  };

  const handleEmergencyExtraChange = (field, value) =>
    setEmergencyFields({ ...emergencyfields, [field]: value });

  const handleEmergencyAddMore = () =>
    setEmergencyFieldCount(emergencyFieldCount + 1);

  const handleEmergencyRemove = () => {
    if (emergencyFieldCount <= 0) return;
    const n = { ...emergencyfields };
    delete n[`name_${emergencyFieldCount}`];
    delete n[`phone_${emergencyFieldCount}`];
    delete n[`relation_${emergencyFieldCount}`];
    setEmergencyFields(n);
    setEmergencyFieldCount(emergencyFieldCount - 1);
  };

  // ── Insurance extra-field helpers ─────────────────────────────────────────

  const handleInsuranceExtraChange = (field, value) =>
    setInsuranceFields({ ...insurancefields, [field]: value });

  const handleInsuranceAddMore = () =>
    setInsuranceFieldCount(insuranceFieldCount + 1);

  const handleInsuranceRemove = () => {
    if (insuranceFieldCount <= 0) return;
    const n = { ...insurancefields };
    delete n[`type_${insuranceFieldCount}`];
    delete n[`company_${insuranceFieldCount}`];
    delete n[`policy_${insuranceFieldCount}`];
    delete n[`phone_${insuranceFieldCount}`];
    setInsuranceFields(n);
    setInsuranceFieldCount(insuranceFieldCount - 1);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (!show) return null;

  const step = ALL_STEPS[currentStep];
  const currentType = step.type;
  const savedItems =
    categoryListing?.find((c) => c.type === currentType)?.dataListing || [];

  return (
    <div className="wizard-overlay">
      <div className="wizard-container">
        {/* ── Header ── */}
        <div className="wizard-header">
          <img
            src={images.wmcLogo}
            alt="WMC"
            height={36}
            style={{ borderRadius: 0 }}
          />
          <h2 className="wizard-title">Order Your Medical Card</h2>
          <button
            className="wizard-logout-btn"
            onClick={() => {
              clearStorage();
              navigate(routePaths.signIn);
            }}
          >
            Log Out
          </button>
        </div>

        {/* ── Step indicators ── */}
        <div className="wizard-steps">
          {ALL_STEPS.map((s, index) => (
            <div
              key={index}
              className={`wizard-step-indicator ${index === currentStep ? "active" : ""} ${index < currentStep ? "completed" : ""}`}
            >
              <div className="step-circle">
                {index < currentStep ? "✓" : index + 1}
              </div>
              <span className="step-label">{s.label}</span>
              {index < ALL_STEPS.length - 1 && (
                <div
                  className={`step-connector ${index < currentStep ? "completed" : ""}`}
                />
              )}
            </div>
          ))}
        </div>

        {/* ── Body ── */}
        <div className="wizard-body">
          {isOrderProcessing || orderLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                flexDirection: "column",
              }}
            >
              <CircularProgress
                size={40}
                color="inherit"
                style={{ marginRight: 8 }}
              />
              <h3>Card Order is In progress</h3>
            </div>
          ) : (
            <div
              className={`wizard-step-content${step.kind === "order" ? " wizard-order-step" : ""}`}
            >
              <div className="step-icon" style={{ background: step.iconBg }}>
                {step.iconImg ? (
                  <img
                    src={step.iconImg}
                    alt={step.label}
                    width={36}
                    height={36}
                  />
                ) : (
                  step.icon
                )}
              </div>
              <h3 className="step-heading">{step.title}</h3>
              <p className="step-subheading">{step.subtitle}</p>

              {/* ── Personal Info ── */}
              {step.id === "personal" && (
                <Formik
                  innerRef={formikRef}
                  initialValues={{
                    firstName: userData?.firstName || "",
                    lastName: userData?.lastName || "",
                    dob: parseBirthDate(userData?.birthDate),
                    gender: userData?.gender || "",
                    nationality: userData?.nationality || "",
                    socialNum: userData?.ssn || "",
                    organDonar: userData?.organDonation || "",
                    relationShip: userData?.relationShip || "",
                  }}
                  validationSchema={
                    hideNameDob
                      ? personalInfoInsuranceSchema
                      : personalInfoModalSchema
                  }
                  onSubmit={handlePersonalInfoSubmit}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                  }) => (
                    <div className="wizard-profile-form">
                      <Grid container spacing={0.7}>
                        {!hideNameDob && (
                          <>
                        <Grid item xs={12} xl={6} lg={6} md={6}>
                          <div className="wf-mb">
                            <MaterialInputField
                              name="firstName"
                              id="firstName"
                              label="First Name *"
                              value={values.firstName}
                              error={touched.firstName && errors.firstName}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} xl={6} lg={6} md={6}>
                          <div className="wf-mb">
                            <MaterialInputField
                              name="lastName"
                              id="lastName"
                              label="Last Name *"
                              value={values.lastName}
                              error={touched.lastName && errors.lastName}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} xl={6} lg={6} md={6}>
                          <div className="wf-datepicker">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <MobileDatePicker
                                label="DOB *"
                                value={values.dob ? dayjs(values.dob) : null}
                                onChange={(newValue) => {
                                  setFieldValue("dob", newValue ? newValue.toDate() : null);
                                }}
                                maxDate={dayjs()}
                                format="DD/MM/YYYY"
                                slotProps={{
                                  textField: {
                                    fullWidth: true,
                                    sx: {
                                      boxShadow: "none",
                                      backgroundColor: "#fafafa",
                                      borderRadius: "10px",
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "10px",
                                      },
                                      "& .MuiOutlinedInput-notchedOutline": {
                                        border: 0,
                                      },
                                      "& .MuiInputBase-input": {
                                        fontSize: "16px",
                                      },
                                    },
                                  },
                                }}
                              />
                            </LocalizationProvider>
                            <div className="wf-error-row">
                              {errors.dob && touched.dob && (
                                <p className="wf-error">{errors.dob}</p>
                              )}
                            </div>
                          </div>
                        </Grid>
                          </>
                        )}
                        <Grid item xs={12} xl={6} lg={6} md={6}>
                          <div className="wf-mb">
                            <Box sx={{ minWidth: 120 }}>
                              <FormControl fullWidth>
                                <InputLabel id="gender-label">
                                  Gender *
                                </InputLabel>
                                <Select
                                  labelId="gender-label"
                                  value={values.gender}
                                  label="Gender"
                                  onChange={handleChange("gender")}
                                  sx={{
                                    boxShadow: "none",
                                    ".MuiOutlinedInput-notchedOutline": {
                                      border: 0,
                                    },
                                  }}
                                >
                                  {GenderList.map(({ value, label }) => (
                                    <MenuItem key={value} value={value}>
                                      {label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                            <div className="wf-error-row">
                              {errors.gender && touched.gender && (
                                <p className="wf-error">{errors.gender}</p>
                              )}
                            </div>
                          </div>
                        </Grid>
                        <Grid item xs={12} xl={6} lg={6} md={6}>
                          <div className="wf-mb">
                            <Box sx={{ minWidth: 120 }}>
                              <FormControl fullWidth>
                                <InputLabel id="nationality-label">
                                  Nationality *
                                </InputLabel>
                                <Select
                                  labelId="nationality-label"
                                  value={values.nationality}
                                  label="Select a nationality"
                                  onChange={handleChange("nationality")}
                                  sx={{
                                    boxShadow: "none",
                                    ".MuiOutlinedInput-notchedOutline": {
                                      border: 0,
                                    },
                                  }}
                                >
                                  {nationality.map((n, i) => (
                                    <MenuItem key={i} value={n}>
                                      {n}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                            <div className="wf-error-row">
                              {errors.nationality && touched.nationality && (
                                <p className="wf-error">{errors.nationality}</p>
                              )}
                            </div>
                          </div>
                        </Grid>
                        <Grid item xs={12} xl={6} lg={6} md={6}>
                          <div className="wf-mb">
                            <Box sx={{ minWidth: 120 }}>
                              <FormControl fullWidth>
                                <InputLabel id="civil-status-label">
                                  Civil Status
                                </InputLabel>
                                <Select
                                  labelId="civil-status-label"
                                  value={values.relationShip}
                                  label="Civil Status"
                                  onChange={handleChange("relationShip")}
                                  sx={{
                                    boxShadow: "none",
                                    ".MuiOutlinedInput-notchedOutline": {
                                      border: 0,
                                    },
                                  }}
                                >
                                  {civilStatus.map((r, i) => (
                                    <MenuItem key={i} value={r}>
                                      {r}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                          </div>
                        </Grid>
                        <Grid item xs={12} xl={6} lg={6} md={6}>
                          <div className="wf-mb">
                            <MaterialInputField
                              name="socialNum"
                              id="socialNum"
                              label="National ID number"
                              value={values.socialNum}
                              error={touched.socialNum && errors.socialNum}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12}>
                          <label style={{ paddingLeft: 10, paddingTop: 10 }}>
                            Would you like to be an organ donor? (optional)
                          </label>
                        </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ minWidth: 120 }}>
                            <FormControl fullWidth>
                              <InputLabel id="organ-donor-label">
                                Organ Donor
                              </InputLabel>
                              <Select
                                labelId="organ-donor-label"
                                value={values.organDonar}
                                label="Organ Donor"
                                onChange={handleChange("organDonar")}
                                sx={{
                                  boxShadow: "none",
                                  ".MuiOutlinedInput-notchedOutline": {
                                    border: 0,
                                  },
                                }}
                              >
                                <MenuItem value={true}>Yes</MenuItem>
                                <MenuItem value={false}>No</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                        </Grid>
                      </Grid>
                    </div>
                  )}
                </Formik>
              )}

              {/* ── Address ── */}
              {step.id === "address" && (
                <Formik
                  innerRef={formikRef}
                  initialValues={{
                    address: userData?.postalAddress?.[0]?.info || "",
                    city: userData?.postalAddress?.[0]?.city || "",
                    state: userData?.postalAddress?.[0]?.state || "",
                    zipCode: userData?.postalAddress?.[0]?.zipCode || "",
                    country: userData?.postalAddress?.[0]?.country || "",
                  }}
                  validationSchema={signUpStep2Schema}
                  onSubmit={handleAddressSubmit}
                >
                  {({ values, errors, touched, handleChange, handleBlur }) => (
                    <div className="wizard-profile-form">
                      <Grid container spacing={0.7}>
                        <Grid item xs={12}>
                          <div className="wf-mb">
                            <MaterialInputField
                              name="address"
                              id="address"
                              label="Address *"
                              value={values.address}
                              error={touched.address && errors.address}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} xl={6} lg={6} md={6}>
                          <div className="wf-mb">
                            <MaterialInputField
                              name="city"
                              id="city"
                              label="City *"
                              value={values.city}
                              error={touched.city && errors.city}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} xl={6} lg={6} md={6}>
                          <div className="wf-mb">
                            <MaterialInputField
                              name="state"
                              id="state"
                              label="State / Province *"
                              value={values.state}
                              error={touched.state && errors.state}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} xl={6} lg={6} md={6}>
                          <div className="wf-mb">
                            <MaterialInputField
                              name="zipCode"
                              id="zipCode"
                              label="Postal / ZIP Code *"
                              value={values.zipCode}
                              error={touched.zipCode && errors.zipCode}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} xl={6} lg={6} md={6}>
                          <div className="wf-mb">
                            <Box sx={{ minWidth: 120 }}>
                              <FormControl fullWidth>
                                <InputLabel id="country-label">
                                  Country *
                                </InputLabel>
                                <Select
                                  labelId="country-label"
                                  label="Country *"
                                  value={
                                    countriesList.some(
                                      (c) => c.value === values.country,
                                    )
                                      ? values.country
                                      : ""
                                  }
                                  onChange={handleChange("country")}
                                  sx={{
                                    boxShadow: "none",
                                    ".MuiOutlinedInput-notchedOutline": {
                                      border: 0,
                                    },
                                  }}
                                >
                                  {countriesList.map(({ value, label }, i) => (
                                    <MenuItem key={i} value={value}>
                                      {label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                            <div className="wf-error-row">
                              {errors.country && touched.country && (
                                <p className="wf-error">{errors.country}</p>
                              )}
                            </div>
                          </div>
                        </Grid>
                      </Grid>
                    </div>
                  )}
                </Formik>
              )}

              {/* ── Insurance ── */}
              {step.id === "insurance" && (
                <Formik
                  innerRef={formikRef}
                  initialValues={{
                    insuranceType: userData?.insurance?.[0]?.type || "",
                    insuranceCompany: userData?.insurance?.[0]?.company || "",
                    policyNumber: userData?.insurance?.[0]?.policy || "",
                    emergencyNumber: userData?.insurance?.[0]?.phone || "",
                  }}
                  onSubmit={handleInsuranceSubmit}
                >
                  {({ values, handleChange, handleBlur }) => (
                    <div className="wizard-profile-form">
                      <Grid container spacing={0.7}>
                        <Grid item xs={12}>
                          <Box sx={{ minWidth: 120 }}>
                            <FormControl fullWidth>
                              <InputLabel id="insurance-type-label">
                                Type of Insurance
                              </InputLabel>
                              <Select
                                labelId="insurance-type-label"
                                value={values.insuranceType}
                                label="Type of Insurance"
                                onChange={handleChange("insuranceType")}
                                sx={{
                                  borderRadius: "8px",
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    border: "1px solid #ccc",
                                  },
                                }}
                              >
                                <MenuItem value="Health">Health</MenuItem>
                                <MenuItem value="Travel">Travel</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <div className="wf-mb">
                            <MaterialInputField
                              name="insuranceCompany"
                              id="insuranceCompany"
                              label="Insurance Company"
                              value={values.insuranceCompany}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12}>
                          <div className="wf-mb">
                            <MaterialInputField
                              name="policyNumber"
                              id="policyNumber"
                              label="Policy Number"
                              value={values.policyNumber}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12}>
                          <div className="wf-mb">
                            <PhoneInputField
                              name="emergencyNumber"
                              id="emergencyNumber"
                              placeholder="Contact Phone"
                              value={values.emergencyNumber}
                              handleChange={handleChange("emergencyNumber")}
                              handleBlur={handleBlur("emergencyNumber")}
                            />
                          </div>
                        </Grid>

                        {Array.from(
                          { length: insuranceFieldCount },
                          (_, idx) => {
                            const suffix = idx + 1;
                            return (
                              <React.Fragment key={idx}>
                                <Grid item xs={12}>
                                  <div className="wf-extra-header">
                                    <span>{`Insurance ${suffix + 1}`}</span>
                                    {idx === insuranceFieldCount - 1 && (
                                      <button
                                        type="button"
                                        className="wf-remove-btn"
                                        onClick={handleInsuranceRemove}
                                      >
                                        Remove
                                      </button>
                                    )}
                                  </div>
                                </Grid>
                                <Grid item xs={12}>
                                  <Box sx={{ minWidth: 120 }}>
                                    <FormControl fullWidth>
                                      <InputLabel>Type of Insurance</InputLabel>
                                      <Select
                                        value={
                                          insurancefields[`type_${suffix}`] ||
                                          ""
                                        }
                                        onChange={(e) =>
                                          handleInsuranceExtraChange(
                                            `type_${suffix}`,
                                            e.target.value,
                                          )
                                        }
                                        sx={{
                                          borderRadius: "8px",
                                          "& .MuiOutlinedInput-notchedOutline":
                                            {
                                              border: "1px solid #ccc",
                                            },
                                        }}
                                      >
                                        <MenuItem value="Health">
                                          Health
                                        </MenuItem>
                                        <MenuItem value="Travel">
                                          Travel
                                        </MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Box>
                                </Grid>
                                <Grid item xs={12}>
                                  <MaterialInputField
                                    name={`company_${suffix}`}
                                    label="Company"
                                    value={
                                      insurancefields[`company_${suffix}`] || ""
                                    }
                                    handleChange={(e) =>
                                      handleInsuranceExtraChange(
                                        `company_${suffix}`,
                                        e.target.value,
                                      )
                                    }
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <MaterialInputField
                                    name={`policy_${suffix}`}
                                    label="Policy"
                                    value={
                                      insurancefields[`policy_${suffix}`] || ""
                                    }
                                    handleChange={(e) =>
                                      handleInsuranceExtraChange(
                                        `policy_${suffix}`,
                                        e.target.value,
                                      )
                                    }
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <PhoneInputField
                                    placeholder="Phone"
                                    value={
                                      insurancefields[`phone_${suffix}`] || ""
                                    }
                                    handleChange={(val) =>
                                      handleInsuranceExtraChange(
                                        `phone_${suffix}`,
                                        val,
                                      )
                                    }
                                  />
                                </Grid>
                              </React.Fragment>
                            );
                          },
                        )}

                        <Grid item xs={12}>
                          <button
                            type="button"
                            className="wf-add-more-btn"
                            onClick={handleInsuranceAddMore}
                          >
                            + Add Another Insurance
                          </button>
                        </Grid>
                      </Grid>
                    </div>
                  )}
                </Formik>
              )}

              {/* ── Emergency Contacts ── */}
              {step.id === "emergency" && (
                <Formik
                  innerRef={formikRef}
                  initialValues={{
                    emergencyName: userData?.emergencyContact?.[0]?.name || "",
                    emergencyPhone:
                      userData?.emergencyContact?.[0]?.phone || "",
                    emergencyRelation:
                      userData?.emergencyContact?.[0]?.relationship || "",
                  }}
                  validate={validateEmergencyForm}
                  onSubmit={handleEmergencySubmit}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    setTouched,
                  }) => (
                    <div className="wizard-profile-form">
                      <Grid container spacing={0.7}>
                        <Grid item xs={12}>
                          <div className="wf-mb">
                            <MaterialInputField
                              name="emergencyName"
                              label="Name *"
                              value={values.emergencyName}
                              error={
                                touched.emergencyName && errors.emergencyName
                              }
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12}>
                          <div className="wf-mb">
                            <PhoneInputField
                              name="emergencyPhone"
                              id="emergencyPhone"
                              placeholder="Phone * (e.g., +1234567890)"
                              value={values.emergencyPhone}
                              handleChange={handleChange("emergencyPhone")}
                              handleBlur={handleBlur("emergencyPhone")}
                              error={
                                touched.emergencyPhone && errors.emergencyPhone
                              }
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12}>
                          <div className="wf-mb">
                            <FormControl fullWidth>
                              <InputLabel>Relationship *</InputLabel>
                              <Select
                                name="emergencyRelation"
                                value={values.emergencyRelation}
                                label="Relationship"
                                onChange={handleChange("emergencyRelation")}
                                onBlur={handleBlur("emergencyRelation")}
                                sx={{
                                  borderRadius: "8px",
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    border: "1px solid #ccc",
                                  },
                                }}
                                error={
                                  touched.emergencyRelation &&
                                  Boolean(errors.emergencyRelation)
                                }
                              >
                                {emergencyRelationshipList.map(
                                  ({ value, label }) => (
                                    <MenuItem key={value} value={value}>
                                      {label}
                                    </MenuItem>
                                  ),
                                )}
                              </Select>
                              {errors.emergencyRelation &&
                                touched.emergencyRelation && (
                                  <p className="wf-error">
                                    {errors.emergencyRelation}
                                  </p>
                                )}
                            </FormControl>
                          </div>
                        </Grid>

                        {Array.from(
                          { length: emergencyFieldCount },
                          (_, idx) => {
                            const suffix = idx + 1;
                            return (
                              <React.Fragment key={idx}>
                                <Grid item xs={12}>
                                  <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mt={2}
                                  >
                                    <Typography fontWeight={600}>
                                      Emergency Contact {suffix + 1}
                                    </Typography>
                                    {idx === emergencyFieldCount - 1 && (
                                      <Button
                                        color="error"
                                        onClick={handleEmergencyRemove}
                                        size="small"
                                        sx={{ textTransform: "none" }}
                                      >
                                        Remove
                                      </Button>
                                    )}
                                  </Box>
                                </Grid>
                                <Grid item xs={12}>
                                  <div className="wf-mb">
                                    <MaterialInputField
                                      name={`name_${suffix}`}
                                      label="Name *"
                                      value={
                                        emergencyfields[`name_${suffix}`] || ""
                                      }
                                      onChange={(e) =>
                                        handleEmergencyExtraChange(
                                          `name_${suffix}`,
                                          e.target.value,
                                        )
                                      }
                                      error={
                                        touched[`name_${suffix}`] &&
                                        errors[`name_${suffix}`]
                                      }
                                    />
                                  </div>
                                </Grid>
                                <Grid item xs={12}>
                                  <div className="wf-mb">
                                    <PhoneInputField
                                      placeholder="Phone * (e.g., +1234567890)"
                                      value={
                                        emergencyfields[`phone_${suffix}`] || ""
                                      }
                                      handleChange={(val) =>
                                        handleEmergencyExtraChange(
                                          `phone_${suffix}`,
                                          val,
                                        )
                                      }
                                      error={
                                        touched[`phone_${suffix}`] &&
                                        errors[`phone_${suffix}`]
                                      }
                                    />
                                  </div>
                                </Grid>
                                <Grid item xs={12}>
                                  <div className="wf-mb">
                                    <FormControl fullWidth>
                                      <InputLabel>Relationship *</InputLabel>
                                      <Select
                                        value={
                                          emergencyfields[
                                            `relation_${suffix}`
                                          ] || ""
                                        }
                                        onChange={(e) =>
                                          handleEmergencyExtraChange(
                                            `relation_${suffix}`,
                                            e.target.value,
                                          )
                                        }
                                        sx={{
                                          borderRadius: "8px",
                                          "& .MuiOutlinedInput-notchedOutline":
                                            {
                                              border: "1px solid #ccc",
                                            },
                                        }}
                                        error={
                                          touched[`relation_${suffix}`] &&
                                          Boolean(errors[`relation_${suffix}`])
                                        }
                                      >
                                        {emergencyRelationshipList.map(
                                          ({ value, label }) => (
                                            <MenuItem key={value} value={value}>
                                              {label}
                                            </MenuItem>
                                          ),
                                        )}
                                      </Select>
                                      {errors[`relation_${suffix}`] &&
                                        touched[`relation_${suffix}`] && (
                                          <p className="wf-error">
                                            {errors[`relation_${suffix}`]}
                                          </p>
                                        )}
                                    </FormControl>
                                  </div>
                                </Grid>
                              </React.Fragment>
                            );
                          },
                        )}

                        <Grid item xs={12}>
                          <Button
                            variant="outlined"
                            fullWidth
                            onClick={handleEmergencyAddMore}
                            sx={{
                              borderStyle: "dashed",
                              borderWidth: "2px",
                              color: "#666",
                              fontWeight: 600,
                              mt: 1,
                              py: 1.2,
                              borderRadius: "8px",
                              textTransform: "none",
                            }}
                          >
                            + Add Another Emergency Contact
                          </Button>
                        </Grid>
                      </Grid>
                    </div>
                  )}
                </Formik>
              )}

              {/* ── Medical steps ── */}
              {step.kind === "medical" && (
                <>
                  {(savedItems.length > 0 || addedInStep.length > 0) && (
                    <div className="wizard-chips">
                      {savedItems.map((item, i) => (
                        <span key={`saved-${i}`} className="chip chip-saved">
                          {item.description}
                        </span>
                      ))}
                      {/* {addedInStep.map((desc, i) => (
                      <span key={`new-${i}`} className='chip chip-new'>{desc}</span>
                    ))} */}
                    </div>
                  )}
                  <div className="wizard-search-row">
                    <input
                      className="wizard-search"
                      placeholder="Search here... (write at least 2 characters)"
                      value={searchedValue}
                      disabled={dataLoader}
                      onChange={({ target: { value } }) => {
                        setSearchedValue(value);
                        clearTimeout(searchDebounceRef.current);
                        if (value.length > 1) {
                          if (currentType === CATEGORIES_TYPES.diagnoses) {
                            searchDebounceRef.current = setTimeout(
                              () => searchFromList(value, currentType),
                              400,
                            );
                          } else {
                            searchFromList(value, currentType);
                          }
                        } else {
                          setFilteredList([]);
                        }
                      }}
                    />
                  </div>
                  <div className="wizard-list">
                    {dataLoader ? (
                      <div className="wizard-loader">
                        <CircularProgress size={28} />
                        <span>{loaderMessage}</span>
                      </div>
                    ) : (
                      filteredList.map(({ description }, i) => (
                        <span
                          key={i}
                          className="wizard-list-item"
                          onClick={() => saveItem(description)}
                        >
                          {description}
                        </span>
                      ))
                    )}
                  </div>

                  {savedItems.length === 0 && addedInStep.length === 0 && (
                    <label className="wizard-confirm-empty">
                      <input
                        type="checkbox"
                        checked={confirmedEmpty}
                        onChange={(e) => {
                          setConfirmedEmpty(e.target.checked);
                          setMedicalNextError(false);
                        }}
                      />
                      I don't have any {step.label.toLowerCase()}
                    </label>
                  )}
                  {medicalNextError && (
                    <p className="wizard-medical-error">
                      Please add at least one item or confirm you don't have any{" "}
                      {step.label.toLowerCase()}.
                    </p>
                  )}
                </>
              )}

              {/* ── Order Card ── */}
              {step.kind === "order" && (
                <>
                  <p className="order-subtitle">
                    Your medical card will include the following information.
                    Click "Order Card" to confirm.
                  </p>
                  <MedicalCard
                    firstName={userData?.firstName}
                    lastName={userData?.lastName}
                    birthDate={userData?.birthDate}
                    organDonation={userData?.organDonation}
                    nationality={userData?.nationality}
                    ssn={userData?.ssn}
                    insuranceCompany={userData?.insurance?.[0]?.company}
                    policyNumber={userData?.insurance?.[0]?.policy}
                    emergencyNumber={userData?.insurance?.[0]?.phone}
                  />
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="wizard-footer">
          <button
            className="wizard-btn wizard-btn-back"
            onClick={handleBack}
            disabled={currentStep === 0 || isOrderProcessing}
          >
            Back
          </button>
          <div className="wizard-footer-right">
            {step.skippable && (
              <button
                className="wizard-btn wizard-btn-skip"
                onClick={advance}
                disabled={isOrderProcessing}
              >
                Skip
              </button>
            )}

            {step.kind === "order" ? (
              <button
                className="wizard-btn wizard-btn-order"
                onClick={handleOrderClick}
                disabled={orderLoading || isOrderProcessing}
              >
                {orderLoading || isOrderProcessing ? (
                  <>
                    <CircularProgress
                      size={18}
                      color="inherit"
                      style={{ marginRight: 8 }}
                    />
                    Processing...
                  </>
                ) : (
                  "Order Card"
                )}
              </button>
            ) : (
              <button
                className="wizard-btn wizard-btn-next"
                onClick={handleNext}
                disabled={profileSaving || isOrderProcessing}
              >
                {profileSaving ? (
                  <>
                    <CircularProgress
                      size={18}
                      color="inherit"
                      style={{ marginRight: 8 }}
                    />
                    Saving...
                  </>
                ) : (
                  "Next"
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
